import { OnInit } from '@angular/core';
import { Utils } from 'formiojs';
import { Component, EventEmitter, Input, Output, AfterViewInit, ViewEncapsulation, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, switchMap } from 'rxjs';
import { TableHeader } from './models/table-header.config';
import { FormioLoader, FormioCustomComponent, ResourceNameService } from 'dp-formio';

@Component({
    selector: 'app-grouped-table-wrapper',
    templateUrl: './grouped-table-wrapper.component.html',
    // styleUrls: ['./grouped-table-wrapper.component.scss']
    encapsulation: ViewEncapsulation.None,
    providers: [FormioLoader]
})
export class GroupedTableWrapperComponent implements FormioCustomComponent<any>, OnInit, OnDestroy, OnChanges {
    dir: 'rtl' | 'ltr';
    @Input() required: boolean;
    @Input() value: any = {};
    @Output() valueChange = new EventEmitter<any>();
    @Input() disabled: boolean;
    @Input() viewOnly: boolean;
    @Input() data: any;
    @Input() customOptions: any;
    @Input() formioComponent: any;
    private subs: Subscription[] = [];
    set sub(s: Subscription) { this.subs.push(s); }

    tableConfig: { cols?: TableHeader[], rows?: any[], rowGroup?: string } = {};
    headerRows = [];
    colsKeys: Array<{ key: string, class?: string, total?: string, editable?: any, ratio: any }> = [];
    rowGroupMetadata: any;
    tableData = [];

    saveControl: {
        justNotes: boolean,
        notes: {
            key: string,
            value: string,
            isRequired: boolean,
        },
        resource: {
            key: string,
            rowId: string,
            generatedKey: string,
            extra: string,
            groupDataOn: string,
            deleteFromBody: string[],
        },
        afterChange: any[]
    } = null;
    notes = '';
    isNotesRequired = true;

    constructor(private translateService: TranslateService,
        private resourceNameService: ResourceNameService,
        private formioLoader: FormioLoader) {
        this.changeDirection(this.translateService.currentLang);
        this.sub = this.translateService.onLangChange.subscribe(data => {
            this.changeDirection(data.lang);
        });
    }
    ngOnInit() {
        // this.sub = this.translateService.onLangChange.subscribe(lang => {
        //     if (lang) {
        //         this.formioComponent.triggerChange();
        //     }
        // });
    }
    ngOnChanges(changes: SimpleChanges) {
        this.formioLoader.loading = false;
        if (this.customOptions) {
            if (this.customOptions.tableConfig && this.customOptions.tableData) {
                this.initView();
            }
        }
    }

    onSaveNotes() {
        this.formioLoader.loading = true;
        if (this.saveControl && this.saveControl.notes) this.saveControl.notes.value = this.notes;
        this.sub = this.resourceNameService.loadCustomByKeyId(this.saveControl.resource.key, this.saveControl.resource.rowId)
            .pipe(switchMap((data: any) => {
                data.data[this.saveControl.notes.key] = this.notes;
                return this.resourceNameService.editSubmission(this.saveControl.resource.key, this.saveControl.resource.rowId, data)
            })).subscribe(row => {
                this.afterChangeValue(row);
                this.formioLoader.loading = false;
            }, error => {
                this.afterChangeValue(null);
                this.formioLoader.loading = false;
            });
    }

    onSaveMap() {
        if (this.saveControl && this.saveControl.notes) this.saveControl.notes.value = this.notes;
        let bodyData = this.groupedDataOn(this.tableData, this.saveControl.resource.groupDataOn, this.saveControl.resource.deleteFromBody);
        if (this.saveControl.resource.extra) {
            const extra = Utils.evaluate(this.saveControl.resource.extra, { submission: this.data, generatedKey: this.saveControl.resource.generatedKey || null, notes: this.notes });
            bodyData = { ...bodyData, ...extra };
        }

        this.formioLoader.loading = true;
        let method = this.resourceNameService.addSubmission(this.saveControl.resource.key, { data: bodyData });
        if (this.saveControl.resource.rowId) {
            method = this.resourceNameService.loadCustomByKeyId(this.saveControl.resource.key, this.saveControl.resource.rowId)
                .pipe(switchMap((data: any) => {
                    data.data = { ...data.data, ...bodyData };
                    return this.resourceNameService.editSubmission(this.saveControl.resource.key, this.saveControl.resource.rowId, data)
                }));
        }

        this.sub = method.subscribe(row => {
            this.saveControl.resource.rowId = row._id;
            this.afterChangeValue(row);
            this.formioLoader.loading = false;
        }, error => {
            this.afterChangeValue(null);
            this.formioLoader.loading = false;
        });
    }

    private groupedDataOn(array: any[], key: string, deleteFromBody: string[] = []) {
        return array.reduce(function (rv, x) {
            const tmep = { ...x };
            if (deleteFromBody && deleteFromBody.length)
                for (const [key, value] of Object.entries(tmep)) {
                    if (deleteFromBody.includes(key)) delete tmep[key];
                }

            (rv[x[key]] = rv[x[key]] || []).push(tmep);
            return rv;
        }, {});
    }

    private afterChangeValue(row) {
        if (this.saveControl.afterChange)
            this.saveControl.afterChange.forEach(item => {
                try {
                    if (this.formioComponent.root.wizard) {
                        const pages = this.formioComponent.root.getFastPages({ all: true, withoutFirstLoad: true });
                        const components = Utils.searchComponents(pages, { key: item.key });
                        components.forEach(c => {
                            if (item.notifyChange) c.triggerChange();
                            else {
                                const value = Utils.evaluate(item.value, { value: this.notes, data: this.data, instance: this.formioComponent, rowId: this.saveControl.resource.rowId, row: row });
                                c.setValue(value);
                            }
                        });
                    } else {
                        const nestedForms = Utils.searchComponents(this.formioComponent.root.components, { type: 'form' });
                        nestedForms.forEach(formComponent => {
                            formComponent.subFormReady.then((subForm) => {
                                const pages = subForm.getFastPages({ all: true, withoutFirstLoad: true });
                                const components = Utils.searchComponents(pages, { key: item.key });
                                components.forEach(c => {
                                    if (item.notifyChange) c.triggerChange();
                                    else {
                                        const value = Utils.evaluate(item.value, { value: this.notes, data: this.data, instance: this.formioComponent, rowId: this.saveControl.resource.rowId, row: row });
                                        c.setValue(value);
                                    }
                                });
                            });
                        });

                        const components = Utils.searchComponents(this.formioComponent.root.components, { key: item.key });
                        components.forEach(c => {

                            if (item.notifyChange) c.triggerChange();
                            else {
                                const value = Utils.evaluate(item.value, { value: this.notes, data: this.data, instance: this.formioComponent, rowId: this.saveControl.resource.rowId, row: row });
                                c.setValue(value);
                            }
                        });
                    }
                } catch (e) {
                    console.log(e);
                }
            });
    }

    private initView() {
        this.tableData = [];
        this.headerRows = [];
        this.colsKeys = [];
        this.saveControl = null;

        this.saveControl = this.customOptions.tableConfig.saveControl;
        if (this.saveControl?.notes?.value) this.notes = this.saveControl.notes.value;
        if (this.saveControl?.notes?.isRequired == false) this.isNotesRequired = false;

        this.tableConfig.cols = this.customOptions.tableConfig.cols as TableHeader[];
        this.tableConfig.rows = this.customOptions.tableConfig.rows ?? [];
        this.tableConfig.rowGroup = this.customOptions.tableConfig.rowGroup;
        this.tableData = this.customOptions.tableData.sort((a, b) => a[this.tableConfig.rowGroup] >= b[this.tableConfig.rowGroup] ? 1 : -1);
        this.initTableHeader();
        this.onSort();
    }

    private initTableHeader() {
        const depth = this.findHeaderDepth();
        this.headerRows = Array.from({ length: depth }, e => []);
        this.tableConfig.cols.forEach(item => {
            this.recursiveFillHeader(item, depth, 0);
        });
    }

    private recursiveFillHeader(item: TableHeader, depth: number, level: number) {
        if (item.childrens) {
            item.colSpan = 0;
            item.childrens.forEach(child => {
                item.colSpan += this.recursiveFillHeader(child, depth, level + 1);
            });
            this.headerRows[level].push(item);
            return item.colSpan;
        } else {
            item.rowSpan = depth - level;
            item.colSpan = 1;
            this.headerRows[level].push(item);
            this.colsKeys.push({ key: item.key, class: item.class, total: item.total, editable: item.editable, ratio: item.ratio });
            return item.colSpan;
        }
    }

    private findHeaderDepth() {
        let depth = 0;
        this.tableConfig.cols.forEach(item => {
            const currentDepth = this.depthLoop(1, item);
            if (currentDepth > depth)
                depth = currentDepth;
        });
        return depth;
    }

    private depthLoop(depth: number, item): number {
        if (item.childrens) {
            let max = 0;
            item.childrens.forEach(child => {
                const result = this.depthLoop(depth + 1, child);
                if (result > max) max = result;
            });
            return max;
        }
        else return depth;
    }


    onSort() { this.updateRowGroupMetaData(); }
    private updateRowGroupMetaData() {
        this.rowGroupMetadata = {};

        if (this.tableData) {
            for (let i = 0; i < this.tableData.length; i++) {
                let rowData = this.tableData[i];
                let groupField = rowData[this.tableConfig.rowGroup];

                if (i == 0) {
                    this.rowGroupMetadata[groupField] = { index: 0, size: 1 };
                }
                else {
                    let previousRowData = this.tableData[i - 1];
                    let previousRowGroup = previousRowData[this.tableConfig.rowGroup];
                    if (groupField === previousRowGroup)
                        this.rowGroupMetadata[groupField].size++;
                    else
                        this.rowGroupMetadata[groupField] = { index: i, size: 1 };
                }
            }
        }
    }

    notifyValue() {
        this.value = { tableData: this.tableData };
        this.valueChange.emit(this.value);
    }

    private changeDirection(lang) {
        this.dir = null;
        setTimeout(() => { this.dir = lang === 'ar' ? 'rtl' : 'ltr'; });
    }
    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
        this.subs = [];
    }

}
